/**
 * The Camarilla ladder for lightweight-charts v4: a series primitive
 * (v4.1+ `series.attachPrimitive`) that paints, per session, under the candles:
 *
 *   the zones   S3–R3 range · R3–R4 / S3–S4 buffers · R4–R5 / S4–S5 breakouts
 *   the levels  one horizontal segment per level per session, so nothing
 *               ever connects one session's ladder to the next
 *   the session boundary (a faint rule) and its date
 *
 * Each session spans its whole skeleton (first slot to last, whitespace
 * included), so the levels cross the full day, as they do in TradingView.
 *
 * and labels the levels of one session -- the last session in view -- on the
 * price axis ("R4 187.37"), with small inline "name price" labels at the right
 * end of every other session's levels.
 *
 * Why a primitive: v4 has no fill-between primitive (an Area series fills to
 * the pane's bottom, a Baseline series to one constant), and a line series
 * does not break on whitespace -- a stepped series per level would draw a
 * vertical connector at every session boundary. Everything here is in the
 * chart's own coordinates (`timeToCoordinate` / `priceToCoordinate`), so it
 * pans and zooms with the candles.
 */

/**
 * The zones, from the TradingView Camarilla indicator's Pine script. Pine's
 * `color.new(c, N)` is N% transparent, so `color.new(#2962ff, 92)` is 8% opaque.
 */
export const BANDS = [
  { from: 's3', to: 'r3', color: 'rgba(41, 98, 255, 0.08)' },  // range, color.new(#2962ff, 92)
  { from: 'r3', to: 'r4', color: 'rgba(255, 152, 0, 0.08)' },  // buffer above, color.new(#ff9800, 92)
  { from: 's4', to: 's3', color: 'rgba(255, 152, 0, 0.08)' },  // buffer below, color.new(#ff9800, 92)
  { from: 'r4', to: 'r5', color: 'rgba(8, 153, 129, 0.12)' },  // bull breakout, color.new(#089981, 88)
  { from: 's5', to: 's4', color: 'rgba(242, 54, 69, 0.12)' },  // bear breakdown, color.new(#f23645, 88)
];

const FONT = '-apple-system, "Segoe UI", Roboto, sans-serif';

export class LadderPrimitive {
  /**
   * @param options.labelRow  which row the period's date label sits in (0 = top), so
   *                          two ladders on one chart (the macro chart's weekly and
   *                          monthly) do not print over each other
   * @param options.boundaries whether to draw the dashed rule at each period start
   * @param options.inlineLabels whether every period but the labelled one prints
   *                          "R4 123.45" at the end of its levels (the macro chart
   *                          shows the hovered period's values in its toolbar instead)
   */
  constructor(options = {}) {
    this._labelRow = Number(options.labelRow) || 0;
    this._boundaries = options.boundaries !== false;
    this._inlineLabels = options.inlineLabels !== false;
    this._chart = null;
    this._series = null;
    this._requestUpdate = null;
    this._sessions = [];
    this._levels = [];
    this._showBands = true;
    this._labelDate = null;
    const self = this;
    this._paneView = {
      zOrder() { return 'bottom'; },
      renderer() { return { draw(target) { self._draw(target); } }; },
    };
  }

  attached({ chart, series, requestUpdate }) {
    this._chart = chart;
    this._series = series;
    this._requestUpdate = requestUpdate;
  }

  detached() {
    this._chart = null;
    this._series = null;
    this._requestUpdate = null;
  }

  paneViews() { return [this._paneView]; }

  updateAllViews() {}

  /** The axis labels: the drawn levels of the session the labels follow. */
  priceAxisViews() {
    const session = this._sessions.find((s) => s.date === this._labelDate && s.levels);
    if (!session || !this._series) return [];
    const series = this._series;
    return this._levels.map((level) => ({
      coordinate: () => series.priceToCoordinate(session.levels[level.key]) ?? -100,
      text: () => `${level.label} ${Number(session.levels[level.key]).toFixed(2)}`,
      textColor: () => '#ffffff',
      backColor: () => level.axisColor || level.color,
      visible: () => true,
      tickVisible: () => true,
    }));
  }

  /**
   * @param sessions  [{ date, firstTime, lastTime, levels|null }] in chart time
   * @param levels    the drawn levels [{ key, label, color, width, dashed }]
   * @param showBands whether to paint the zones
   */
  setData(sessions, levels, showBands) {
    this._sessions = sessions || [];
    this._levels = levels || [];
    this._showBands = showBands !== false;
    if (!this._sessions.some((s) => s.date === this._labelDate)) {
      const last = [...this._sessions].reverse().find((s) => s.levels);
      this._labelDate = last ? last.date : null;
    }
    this._update();
  }

  /** Follow a session with the axis labels (the last one in view). */
  setLabelSession(date) {
    if (date && date !== this._labelDate) {
      this._labelDate = date;
      this._update();
    }
  }

  _update() {
    if (this._requestUpdate) this._requestUpdate();
  }

  _draw(target) {
    if (!this._chart || !this._series || !this._sessions.length) return;
    const timeScale = this._chart.timeScale();
    const half = (timeScale.options().barSpacing || 6) / 2;
    const series = this._series;
    target.useBitmapCoordinateSpace(({ context: ctx, horizontalPixelRatio: hr, verticalPixelRatio: vr, bitmapSize }) => {
      this._sessions.forEach((s, i) => {
        const a = timeScale.timeToCoordinate(s.firstTime);
        const b = timeScale.timeToCoordinate(s.lastTime);
        if (a === null || b === null) return;
        const x0 = Math.round((a - half) * hr);
        const x1 = Math.round((b + half) * hr);
        if (x1 < 0 || x0 > bitmapSize.width) return;

        if (s.levels && this._showBands) {
          for (const band of BANDS) {
            const yA = series.priceToCoordinate(s.levels[band.from]);
            const yB = series.priceToCoordinate(s.levels[band.to]);
            if (yA === null || yB === null) continue;
            const top = Math.round(Math.min(yA, yB) * vr);
            ctx.fillStyle = band.color;
            ctx.fillRect(x0, top, x1 - x0, Math.round(Math.max(yA, yB) * vr) - top);
          }
        }

        if (s.levels) {
          for (const level of this._levels) {
            const y = series.priceToCoordinate(s.levels[level.key]);
            if (y === null) continue;
            const yy = Math.round(y * vr) + 0.5;
            ctx.strokeStyle = level.color;
            ctx.lineWidth = Math.max(1, Math.round((level.width || 1) * hr));
            ctx.setLineDash(level.dashed ? [5 * hr, 4 * hr] : []);
            ctx.beginPath();
            ctx.moveTo(x0, yy);
            ctx.lineTo(x1, yy);
            ctx.stroke();
          }
          ctx.setLineDash([]);
          if (s.date !== this._labelDate && this._inlineLabels) {
            ctx.font = `${Math.round(10 * vr)}px ${FONT}`;
            ctx.textAlign = 'right';
            ctx.textBaseline = 'bottom';
            for (const level of this._levels) {
              const y = series.priceToCoordinate(s.levels[level.key]);
              if (y === null) continue;
              const text = `${level.label} ${Number(s.levels[level.key]).toFixed(2)}`;
              // A span narrower than its label (a week on a daily chart) is left unlabelled.
              if (ctx.measureText(text).width + 8 * hr > x1 - x0) continue;
              ctx.fillStyle = level.color;
              ctx.fillText(text, x1 - 4 * hr, Math.round(y * vr) - 3 * vr);
            }
          }
        }

        if (i > 0 && this._boundaries) {
          ctx.strokeStyle = 'rgba(19, 23, 34, 0.18)';
          ctx.lineWidth = Math.max(1, Math.round(hr));
          ctx.setLineDash([4 * hr, 4 * hr]);
          ctx.beginPath();
          ctx.moveTo(x0 + 0.5, 0);
          ctx.lineTo(x0 + 0.5, bitmapSize.height);
          ctx.stroke();
          ctx.setLineDash([]);
        }
        ctx.font = `${Math.round(11 * vr)}px ${FONT}`;
        ctx.fillStyle = '#787b86';
        ctx.textAlign = 'left';
        ctx.textBaseline = 'top';
        const name = s.levels ? s.date : `${s.date} · no ladder`;
        if (ctx.measureText(name).width + 12 * hr <= x1 - x0) {
          ctx.fillText(name, x0 + 6 * hr, (6 + 14 * this._labelRow) * vr);
        }
      });
    });
  }
}
